(function (root) {
  'use strict';

  var MEDIA_IMAGE_GID_PREFIX = 'gid://shopify/MediaImage/';
  var PRODUCT_GID_PREFIX = 'gid://shopify/Product/';
  var DEFAULT_MAX_IDS_PER_REQUEST = 250;
  var AUTOCOMPLETE_CACHE_LIMIT = 10;
  var mediaImageCache = Object.create(null);
  var mediaImageInFlightById = Object.create(null);
  var productGalleryCache = Object.create(null);
  var productGalleryInFlightById = Object.create(null);
  var autocompleteCache = Object.create(null);
  var miniSearchTimer = null;
  var miniSearchPendingQuery = '';
  var miniSearchImageRequestId = 0;

  function hasOwn(object, key) {
    return Object.prototype.hasOwnProperty.call(object, key);
  }

  function isMediaImageGid(value) {
    return typeof value === 'string' && value.indexOf(MEDIA_IMAGE_GID_PREFIX) === 0;
  }

  function isProductGid(value) {
    return typeof value === 'string' && value.indexOf(PRODUCT_GID_PREFIX) === 0;
  }

  function uniqueIds(ids, validator) {
    var seen = Object.create(null);

    return (ids || []).filter(function (id) {
      if (!validator(id) || seen[id]) {
        return false;
      }

      seen[id] = true;
      return true;
    });
  }

  function getConfig() {
    return root.SdgSearchSpringMediaGalleryConfig || {};
  }

  function requestJson(url, options) {
    var config = getConfig();
    var controller = typeof root.AbortController === 'function' ? new root.AbortController() : null;
    var timeoutId = null;
    var timeoutMs = Number(config.requestTimeoutMs) || 4000;
    var requestOptions = options || {};

    if (controller) {
      requestOptions.signal = controller.signal;
      timeoutId = root.setTimeout(function () {
        controller.abort();
      }, timeoutMs);
    }

    return root.fetch(url, requestOptions)
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Request failed with status ' + response.status);
        }

        return response.json();
      })
      .then(function (payload) {
        if (timeoutId) {
          root.clearTimeout(timeoutId);
        }

        return payload;
      }, function (error) {
        if (timeoutId) {
          root.clearTimeout(timeoutId);
        }

        throw error;
      });
  }

  function requestStorefront(query, variables) {
    var config = getConfig();

    if (!config.endpoint || !config.publicToken || typeof root.fetch !== 'function') {
      return Promise.resolve({ data: {} });
    }

    return requestJson(config.endpoint, {
      method: 'POST',
      mode: 'cors',
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': config.publicToken
      },
      body: JSON.stringify({
        query: query,
        variables: variables
      })
    }).then(function (payload) {
      if (payload.errors && payload.errors.length) {
        throw new Error('Storefront API returned GraphQL errors');
      }

      return payload;
    });
  }

  function requestMediaImages(ids, maxWidth) {
    if (!ids.length) {
      return Promise.resolve({});
    }

    var imageWidth = maxWidth === 500 ? 500 : 700;

    var query = [
      'query ResolveGalleryImages($ids: [ID!]!) {',
      '  nodes(ids: $ids) {',
      '    ... on MediaImage {',
      '      id',
      '      image {',
      '        url(transform: { maxWidth: ' + imageWidth + ' })',
      '      }',
      '    }',
      '  }',
      '}'
    ].join('\n');

    return requestStorefront(query, { ids: ids }).then(function (payload) {
      var resolved = Object.create(null);
      var nodes = payload.data && payload.data.nodes ? payload.data.nodes : [];

      nodes.forEach(function (node) {
        if (!node || !node.id || !node.image || !node.image.url) {
          return;
        }

        resolved[node.id] = {
          url: node.image.url
        };
      });

      return resolved;
    });
  }

  function requestProductGalleryImages(ids) {
    if (!ids.length) {
      return Promise.resolve({});
    }

    var query = [
      'query ResolveProductGalleryImages($ids: [ID!]!) {',
      '  nodes(ids: $ids) {',
      '    ... on Product {',
      '      id',
      '      metafield(namespace: "normakamali", key: "media_gallery") {',
      '        value',
      '      }',
      '    }',
      '  }',
      '}'
    ].join('\n');

    return requestStorefront(query, { ids: ids }).then(function (payload) {
      var resolved = Object.create(null);
      var nodes = payload.data && payload.data.nodes ? payload.data.nodes : [];
      var productImageIds = Object.create(null);
      var imageIds = [];

      nodes.forEach(function (node) {
        var galleryIds = parseGalleryIds(node && node.metafield && node.metafield.value);
        // Native recommendation tiles must agree with their server-rendered
        // image, which is the first usable gallery image. The autocomplete
        // picker intentionally has a different editorial-image fallback.
        var imageId = galleryIds[0];

        if (!node || !node.id || !imageId) {
          return;
        }

        productImageIds[node.id] = imageId;
        imageIds.push(imageId);
      });

      return requestMediaImages(uniqueIds(imageIds, isMediaImageGid), 500).then(function (images) {
        Object.keys(productImageIds).forEach(function (productId) {
          var imageId = productImageIds[productId];

          if (images[imageId]) {
            resolved[productId] = images[imageId];
          }
        });

        return resolved;
      });
    });
  }

  function collectCached(ids, cache) {
    var resolved = Object.create(null);

    ids.forEach(function (id) {
      if (cache[id]) {
        resolved[id] = cache[id];
      }
    });

    return resolved;
  }

  function resolveBatched(ids, validator, cache, inFlightById, request) {
    var requestedIds = uniqueIds(ids, validator);

    if (!requestedIds.length) {
      return Promise.resolve({});
    }

    var config = getConfig();
    var maxIdsPerRequest = Math.min(
      Number(config.maxIdsPerRequest) || DEFAULT_MAX_IDS_PER_REQUEST,
      DEFAULT_MAX_IDS_PER_REQUEST
    );
    var missingIds = requestedIds.filter(function (id) {
      return !hasOwn(cache, id) && !inFlightById[id];
    });
    var waits = [];

    requestedIds.forEach(function (id) {
      if (inFlightById[id] && waits.indexOf(inFlightById[id]) === -1) {
        waits.push(inFlightById[id]);
      }
    });

    for (var offset = 0; offset < missingIds.length; offset += maxIdsPerRequest) {
      (function (batch) {
        var pending = request(batch).then(function (resolved) {
          batch.forEach(function (id) {
            cache[id] = resolved[id] || null;
            delete inFlightById[id];
          });
        }, function (error) {
          batch.forEach(function (id) {
            delete inFlightById[id];
          });

          throw error;
        });

        batch.forEach(function (id) {
          inFlightById[id] = pending;
        });
        waits.push(pending);
      })(missingIds.slice(offset, offset + maxIdsPerRequest));
    }

    return Promise.all(waits).then(function () {
      return collectCached(requestedIds, cache);
    });
  }

  function resolve(ids) {
    return resolveBatched(
      ids,
      isMediaImageGid,
      mediaImageCache,
      mediaImageInFlightById,
      requestMediaImages
    );
  }

  function resolveProductGalleries(ids) {
    return resolveBatched(
      ids,
      isProductGid,
      productGalleryCache,
      productGalleryInFlightById,
      requestProductGalleryImages
    );
  }

  function isInHiddenFallback(target) {
    return typeof target.closest === 'function' && !!target.closest('.native-recs-fallback[hidden]');
  }

  function applyImage(target, url) {
    if (String(target.tagName || '').toLowerCase() === 'img' || typeof target.src !== 'undefined') {
      target.src = url;
      return;
    }

    if (target.style) {
      target.style.backgroundImage = 'url("' + String(url).replace(/["\\\n\r]/g, '\\$&') + '")';
    }
  }

  function preloadImage(url) {
    if (typeof root.Image !== 'function') {
      return Promise.resolve(true);
    }

    return new Promise(function (resolve) {
      var image = new root.Image();
      var settled = false;
      var timeoutId = null;
      var finish = function (loaded) {
        if (settled) {
          return;
        }

        settled = true;

        if (timeoutId && typeof root.clearTimeout === 'function') {
          root.clearTimeout(timeoutId);
        }

        resolve(loaded);
      };

      image.onload = function () {
        finish(true);
      };
      image.onerror = function () {
        finish(false);
      };

      if (typeof root.setTimeout === 'function') {
        timeoutId = root.setTimeout(function () {
          finish(false);
        }, Number(getConfig().imagePreloadTimeoutMs) || 2000);
      }

      image.src = url;

      if (image.complete && image.naturalWidth) {
        finish(true);
      }
    });
  }

  function markTargetsAsFallback(targets, resolvedAttribute) {
    targets.forEach(function (target) {
      if (target.isConnected !== false) {
        target.setAttribute(resolvedAttribute, 'fallback');
      }
    });
  }

  function hydrateTargets(targets, attribute, resolvedAttribute, resolver, cache) {
    if (!targets.length) {
      return Promise.resolve(false);
    }

    var ids = targets.map(function (target) {
      return target.getAttribute(attribute);
    });

    return resolver(ids).then(function (resolved) {
      var updates = targets.map(function (target) {
        var id = target.getAttribute(attribute);
        var image = resolved[id];

        if (target.isConnected === false || !image || !image.url) {
          if (target.isConnected !== false && hasOwn(cache, id)) {
            target.setAttribute(resolvedAttribute, 'fallback');
          }
          return Promise.resolve();
        }

        return preloadImage(image.url).then(function (loaded) {
          if (
            target.isConnected === false
            || target.getAttribute(attribute) !== id
          ) {
            return;
          }

          if (!loaded) {
            target.setAttribute(resolvedAttribute, 'fallback');
            return;
          }

          applyImage(target, image.url);
          target.setAttribute(resolvedAttribute, 'true');
        });
      });

      return Promise.all(updates).then(function () {
        return true;
      });
    });
  }

  function hydrate(container) {
    if (!container || typeof container.querySelectorAll !== 'function') {
      return Promise.resolve(false);
    }

    var mediaTargets = Array.prototype.slice.call(
      container.querySelectorAll('[data-sdg-media-gallery-gid]:not([data-sdg-media-gallery-resolved])')
    ).filter(function (target) {
      return !isInHiddenFallback(target);
    });
    var productTargets = Array.prototype.slice.call(
      container.querySelectorAll('[data-sdg-product-gallery-id]:not([data-sdg-product-gallery-resolved])')
    ).filter(function (target) {
      return !isInHiddenFallback(target);
    });

    if (!mediaTargets.length && !productTargets.length) {
      return Promise.resolve(false);
    }

    return Promise.all([
      hydrateTargets(
        mediaTargets,
        'data-sdg-media-gallery-gid',
        'data-sdg-media-gallery-resolved',
        resolve,
        mediaImageCache
      ),
      hydrateTargets(
        productTargets,
        'data-sdg-product-gallery-id',
        'data-sdg-product-gallery-resolved',
        resolveProductGalleries,
        productGalleryCache
      )
    ]).then(function () {
      return true;
    }).catch(function () {
      // Reveal the native fallback if the API is unavailable. Newly rendered
      // SearchSpring or recommendation targets can still retry later.
      markTargetsAsFallback(mediaTargets, 'data-sdg-media-gallery-resolved');
      markTargetsAsFallback(productTargets, 'data-sdg-product-gallery-resolved');
      return false;
    });
  }

  function pickAutocompleteGalleryId(galleryIds) {
    if (!galleryIds.length) {
      return null;
    }

    // The curated normakamali.media_gallery metafield is the source of truth
    // for product imagery across autocomplete, PLP/PDP, and recommendations.
    // Use its first usable image rather than skipping it by position.
    return galleryIds[0];
  }

  function parseGalleryIds(value) {
    if (value === null || typeof value === 'undefined') {
      return [];
    }

    if (Array.isArray(value)) {
      return uniqueIds(value, isMediaImageGid);
    }

    if (typeof value === 'object' && hasOwn(value, 'value')) {
      return parseGalleryIds(value.value);
    }

    var decoded = String(value)
      .replace(/&(?:amp;)?quot;|&#0*34;/gi, '"')
      .replace(/&#0*39;|&apos;/gi, "'")
      .replace(/&amp;/gi, '&');
    var matches = decoded.match(/gid:\/\/shopify\/MediaImage\/\d+/g);

    return uniqueIds(matches || [], isMediaImageGid);
  }

  function indexedGalleryValue(result) {
    if (!result) {
      return null;
    }

    return result.mfield_normakamali_media_gallery
      || result.mtfld_normakamali_media_gallery
      || result.normakamali_media_gallery
      || result['normakamali.media_gallery']
      || null;
  }

  function autocompleteImageUrls(result) {
    var value = result && (result.ss_images || result.images);

    if (!value) {
      return [];
    }

    return String(value).split(',').map(function (url) {
      return url.trim();
    }).filter(function (url) {
      return /^https?:\/\//i.test(url) || /^\/\//.test(url);
    });
  }

  function pickAutocompleteImageUrl(result) {
    var urls = autocompleteImageUrls(result);

    // When the curated gallery metafield is empty, fall back to the first
    // product image indexed by SearchSpring.
    return urls[0] || '';
  }

  function productHandle(value) {
    if (!value) {
      return '';
    }

    // SearchSpring's result URL is what its Angular autocomplete renderer uses
    // for the card link. Prefer it over `handle`, which can be stale when an
    // index record has been reused for a preview result.
    var url = typeof value === 'string' ? value : value.url;
    var match = String(url || '').match(/\/products\/([^/?#]+)/i);

    if (match) {
      try {
        return decodeURIComponent(match[1]).toLowerCase();
      } catch (error) {
        return match[1].toLowerCase();
      }
    }

    return value.handle ? String(value.handle).toLowerCase() : '';
  }

  function activeAutocompleteQuery(container, fallbackQuery) {
    var activeLink = container && typeof container.querySelector === 'function'
      ? container.querySelector('.ss-list-option.ss-active a[href*="q="]')
      : null;
    var href = activeLink && typeof activeLink.getAttribute === 'function'
      ? activeLink.getAttribute('href')
      : null;
    var match = String(href || '').match(/[?&]q=([^&#]+)/i);

    if (!match) {
      return fallbackQuery;
    }

    try {
      return decodeURIComponent(match[1].replace(/\+/g, ' '));
    } catch (error) {
      return match[1].replace(/\+/g, ' ');
    }
  }

  function miniSearchCardMatchesRequest(container, card, image, query, handle, requestId) {
    if (!card || !image || card.isConnected === false || image.isConnected === false) {
      return false;
    }

    var input = typeof container.querySelector === 'function'
      ? container.querySelector('input.searchspring-ac')
      : null;
    var typedQuery = input && String(input.value || '').trim();
    var currentQuery = activeAutocompleteQuery(container, typedQuery);
    var currentLink = typeof card.querySelector === 'function'
      ? card.querySelector('a[href*="/products/"]')
      : null;
    var currentHandle = productHandle(currentLink && currentLink.getAttribute('href'));

    return currentQuery === query
      && currentHandle === handle
      && card.getAttribute('data-sdg-media-gallery-query') === query
      && card.getAttribute('data-sdg-media-gallery-handle') === handle
      && image.getAttribute('data-sdg-media-gallery-request') === requestId;
  }

  function requestAutocomplete(query) {
    var config = getConfig();
    var siteId = config.searchSpringSiteId;
    var cacheKey = String(query || '').trim().toLowerCase();

    if (!cacheKey || !siteId || typeof root.fetch !== 'function') {
      return Promise.resolve([]);
    }

    if (autocompleteCache[cacheKey]) {
      return autocompleteCache[cacheKey];
    }

    if (Object.keys(autocompleteCache).length >= AUTOCOMPLETE_CACHE_LIMIT) {
      delete autocompleteCache[Object.keys(autocompleteCache)[0]];
    }

    var endpoint = 'https://' + encodeURIComponent(siteId)
      + '.a.searchspring.io/api/search/autocomplete.json?siteId=' + encodeURIComponent(siteId)
      + '&resultsFormat=json&q=' + encodeURIComponent(query);

    autocompleteCache[cacheKey] = requestJson(endpoint, {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit'
    }).then(function (payload) {
      return payload && Array.isArray(payload.results) ? payload.results : [];
    }).catch(function (error) {
      delete autocompleteCache[cacheKey];
      throw error;
    });

    return autocompleteCache[cacheKey];
  }

  function markMiniSearchPending(container, query) {
    var pendingQuery = String(query || '').trim();

    if (!pendingQuery || !container || !container.classList) {
      return;
    }

    miniSearchPendingQuery = pendingQuery;
    container.classList.add('sdg-media-gallery-pending');
  }

  function resetMiniSearchImages(container) {
    // SearchSpring reuses result-card nodes as the query changes. Remove the
    // previous resolved marker when its product link changes so the next
    // inline `ss_image` cannot paint before the alternate image has preloaded.
    if (typeof container.querySelectorAll === 'function') {
      Array.prototype.forEach.call(
        container.querySelectorAll('.ss-ac-item'),
        function (card) {
          card.removeAttribute('data-sdg-media-gallery-query');
          card.removeAttribute('data-sdg-media-gallery-handle');
        }
      );
      Array.prototype.forEach.call(
        container.querySelectorAll('.ss-ac-item .ss-image-wrapper'),
        function (image) {
          image.removeAttribute('data-sdg-media-gallery-gid');
          image.removeAttribute('data-sdg-media-gallery-resolved');
          image.removeAttribute('data-sdg-media-gallery-request');
        }
      );
    }
  }

  function clearMiniSearchPending(container, query) {
    if (query && miniSearchPendingQuery !== query) {
      return;
    }

    miniSearchPendingQuery = '';

    if (container && container.classList) {
      container.classList.remove('sdg-media-gallery-pending');
    }
  }

  function hydrateMiniSearch(container) {
    if (!container || typeof container.querySelectorAll !== 'function') {
      return Promise.resolve(false);
    }

    var input = typeof container.querySelector === 'function'
      ? container.querySelector('input.searchspring-ac')
      : null;
    var typedQuery = input && String(input.value || '').trim();
    var query = activeAutocompleteQuery(container, typedQuery);
    var cards = Array.prototype.slice.call(container.querySelectorAll('.ss-ac-item'));

    if (!query || !cards.length) {
      clearMiniSearchPending(container);
      return Promise.resolve(false);
    }

    var pendingCards = cards.filter(function (card) {
      var link = typeof card.querySelector === 'function'
        ? card.querySelector('a[href*="/products/"]')
        : null;
      var handle = productHandle(link && link.getAttribute('href'));

      return handle && (
        card.getAttribute('data-sdg-media-gallery-query') !== query
        || card.getAttribute('data-sdg-media-gallery-handle') !== handle
      );
    });

    if (!pendingCards.length) {
      return hydrate(container).then(function (hydrated) {
        clearMiniSearchPending(container, query);
        return hydrated;
      });
    }

    markMiniSearchPending(container, query);

    var pendingHydration = requestAutocomplete(query).then(function (results) {
      var currentInput = typeof container.querySelector === 'function'
        ? container.querySelector('input.searchspring-ac')
        : null;
      var currentTypedQuery = currentInput && String(currentInput.value || '').trim();
      var currentQuery = activeAutocompleteQuery(container, currentTypedQuery);

      if (currentQuery !== query) {
        return false;
      }

      var resultsByHandle = Object.create(null);

      results.forEach(function (result) {
        var handle = productHandle(result);

        if (handle) {
          resultsByHandle[handle] = result;
        }
      });

      var indexedGalleriesByHandle = Object.create(null);
      var imageUpdates = [];

      Object.keys(resultsByHandle).forEach(function (handle) {
        indexedGalleriesByHandle[handle] = parseGalleryIds(indexedGalleryValue(resultsByHandle[handle]));
      });

      pendingCards.forEach(function (card) {
        if (card.isConnected === false) {
          return;
        }

        var link = card.querySelector('a[href*="/products/"]');
        var image = card.querySelector('.ss-image-wrapper');
        var handle = productHandle(link && link.getAttribute('href'));
        var result = resultsByHandle[handle];
        var imageUrl = pickAutocompleteImageUrl(result);
        var galleryIds = indexedGalleriesByHandle[handle] || [];
        var galleryId = pickAutocompleteGalleryId(galleryIds);

        card.setAttribute('data-sdg-media-gallery-query', query);
        card.setAttribute('data-sdg-media-gallery-handle', handle);

        if (!image) {
          return;
        }

        image.removeAttribute('data-sdg-media-gallery-gid');
        image.removeAttribute('data-sdg-media-gallery-resolved');
        image.removeAttribute('data-sdg-media-gallery-request');

        if (galleryId) {
          image.setAttribute('data-sdg-media-gallery-gid', galleryId);
          return;
        }

        if (imageUrl) {
          var requestId = String(++miniSearchImageRequestId);
          image.setAttribute('data-sdg-media-gallery-request', requestId);

          (function (targetCard, target, url, expectedQuery, expectedHandle, expectedRequestId) {
            imageUpdates.push(preloadImage(url).then(function (loaded) {
              if (!miniSearchCardMatchesRequest(
                container,
                targetCard,
                target,
                expectedQuery,
                expectedHandle,
                expectedRequestId
              )) {
                return;
              }

              target.removeAttribute('data-sdg-media-gallery-request');

              if (loaded) {
                applyImage(target, url);
                target.setAttribute('data-sdg-media-gallery-resolved', 'true');
              } else {
                target.setAttribute('data-sdg-media-gallery-resolved', 'fallback');
              }
            }));
          })(card, image, imageUrl, query, handle, requestId);
          return;
        }

        // No override exists for this result. Explicitly reveal SearchSpring's
        // native thumbnail rather than leaving the card in a pending state.
        image.setAttribute('data-sdg-media-gallery-resolved', 'fallback');
      });

      return Promise.all(imageUpdates).then(function () {
        return hydrate(container).then(function (hydrated) {
          return hydrated || imageUpdates.length > 0;
        });
      });
    });

    return pendingHydration.then(function (hydrated) {
      clearMiniSearchPending(container, query);
      return hydrated;
    }, function () {
      clearMiniSearchPending(container, query);
      return false;
    });
  }

  function initMiniSearch() {
    if (!root.document || !getConfig().searchSpringSiteId) {
      return;
    }

    var container = root.document.getElementById('SearchBar');

    if (!container) {
      return;
    }

    var schedule = function () {
      var input = typeof container.querySelector === 'function'
        ? container.querySelector('input.searchspring-ac')
        : null;

      markMiniSearchPending(container, input && input.value);

      if (miniSearchTimer) {
        root.clearTimeout(miniSearchTimer);
      }

      miniSearchTimer = root.setTimeout(function () {
        hydrateMiniSearch(container);
      }, 75);
    };

    if (typeof container.addEventListener === 'function') {
      container.addEventListener('input', schedule);
      // SearchSpring changes the preview on suggestion hover/focus without
      // changing the search input. Schedule after those interactions so the
      // gallery lookup follows the active suggestion, too.
      container.addEventListener('keydown', schedule);
      container.addEventListener('focusin', schedule);
      container.addEventListener('mouseover', function (event) {
        var target = event.target;

        if (target && typeof target.closest === 'function' && target.closest('.ss-list-link')) {
          schedule();
        }
      });
    }

    if (typeof root.MutationObserver === 'function') {
      var observer = new root.MutationObserver(function (records) {
        var productLinkChanged = records.some(function (record) {
          return record.type === 'attributes' && record.attributeName === 'href';
        });

        if (productLinkChanged) {
          resetMiniSearchImages(container);
        }

        schedule();
      });
      observer.observe(container, {
        childList: true,
        subtree: true,
        // Angular reuses autocomplete card nodes and changes their links when
        // a new suggestion preview arrives. Watching href changes catches that
        // update before the browser paints the corresponding native ss_image.
        attributes: true,
        attributeFilter: ['href']
      });
    }

    schedule();
  }

  root.SdgSearchSpringMediaGallery = {
    hydrate: hydrate,
    hydrateMiniSearch: hydrateMiniSearch,
    isMediaImageGid: isMediaImageGid,
    parseGalleryIds: parseGalleryIds,
    resolve: resolve,
    resolveProductGalleries: resolveProductGalleries
  };

  function init() {
    hydrate(root.document);
    initMiniSearch();
  }

  if (root.document && typeof root.document.addEventListener === 'function') {
    if (root.document.readyState === 'loading') {
      root.document.addEventListener('DOMContentLoaded', init, { once: true });
    } else {
      init();
    }
  }
})(window);
